import React, { useCallback, useEffect, useMemo, useState } from 'react';
import LicenseList from './_components/LicenseList';
import { ILicenseSimpleProps, ILicenseUpdatePayload } from '../../services/LicenseService/types';
import { getAllLicensesAsync, getCurrentLicenseAsync, updateLicenseAsync } from '../../services/LicenseService';
import { getDashboardSummaryAsync } from '../../services/Dashboard';
import { t } from 'i18next';
import { getPermissions } from '../../services/DefaultService';
import { Permissions } from '../../services/enums';

interface LicenseLiveCounters {
    devicesAmount: number;
    studentsAmount: number;
    responsiblesAmount: number;
}

const LicensePage: React.FC = () => {
    const [licenses, setLicenses] = useState<ILicenseSimpleProps[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'all' | 'current'>('current');
    const [liveCounters, setLiveCounters] = useState<LicenseLiveCounters | null>(null);

    const hasAllPermission = useCallback((): boolean => {
        const permissions = getPermissions();

        if (!permissions) return false;

        if (Array.isArray(permissions)) {
            return permissions.some((permission) => permission?.toUpperCase() === Permissions.ALL);
        }

        return permissions
            .split(',')
            .map((permission) => permission.trim().toUpperCase())
            .includes(Permissions.ALL);
    }, []);

    const canEditValues = useMemo(() => hasAllPermission(), [hasAllPermission]);

    const fetchLicenses = useCallback(async () => {
        try {
            if (canEditValues) {
                const allLicenses = await getAllLicensesAsync();

                setLiveCounters(null);
                setLicenses(allLicenses);
                setViewMode('all');
                return;
            }

            const [currentLicense, dashboardSummary] = await Promise.all([
                getCurrentLicenseAsync(),
                getDashboardSummaryAsync(),
            ]);

            setLiveCounters({
                devicesAmount: dashboardSummary?.counters?.devices ?? 0,
                studentsAmount: dashboardSummary?.counters?.students ?? 0,
                responsiblesAmount: dashboardSummary?.counters?.responsibles ?? 0,
            });

            setLicenses(currentLicense ? [currentLicense] : []);
            setViewMode('current');
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [canEditValues]);

    const handleUpdateLicenseValues = useCallback(async (id: string, payload: ILicenseUpdatePayload) => {
        await updateLicenseAsync(id, payload);
        await fetchLicenses();
    }, [fetchLicenses]);

    useEffect(() => {
        void fetchLicenses();
    }, [fetchLicenses]);

    return (
        <div>
            {viewMode === 'current' && <h1>{t('financial.licenses')}</h1>}

            {loading && <p>Carregando...</p>}
            {error && <p>Erro: {error}</p>}

            <LicenseList
                licenses={licenses}
                isSingleMode={viewMode === 'current'}
                liveCounters={liveCounters}
                canEditValues={canEditValues}
                onUpdateLicenseValues={canEditValues ? handleUpdateLicenseValues : undefined}
            />
        </div>
    );
};

export default LicensePage;
